import React from 'react';
import { Formik, Form } from 'formik';

import Modal from 'components/layout/Modal';
import { View } from 'components/layout/View';
import Text from 'components/outputs/Text';
import Input from 'components/inputs/Input';
import ErrorOutput from 'components/outputs/Error';
import ButtonList from 'components/lists/ButtonList';
import PlaceholderImage from 'components/outputs/PlaceholderImage';
import * as inputs from 'config/inputs';
import { verifyMFA, deliverTokenForMFA } from 'util/rehive';

// Generic step-up MFA prompt. Driven entirely by MfaChallengeContext: it renders
// whenever a `challenge` is present, verifies the OTP against the active session,
// then hands control back via onSuccess/onCancel. Reuses the OTP markup from
// screens/auth/components/MfaVerifyPage but without the login-flow coupling.
export default function MfaChallengeModal({ challenge, onSuccess, onCancel }) {
  const authenticatorTypes = challenge?.authenticator_types ?? [];
  const isTotp = authenticatorTypes.includes('totp');
  // Offer SMS resend only when SMS is the (non-TOTP) factor in play.
  const isSms = !isTotp && authenticatorTypes.includes('sms');

  function handleError(e, actions) {
    actions.setFieldValue('otp', '');
    actions.setStatus({ error: e?.message || 'error' });
    actions.setSubmitting(false);
  }

  async function handleSubmit(values, actions) {
    if (!actions || actions.isSubmitting) {
      return;
    }
    actions.setSubmitting(true);
    const { otp } = values;
    try {
      // No customToken: the active session's authFetch attaches the token.
      const resp = await verifyMFA({ token: otp, challenge: challenge.id });
      if (resp?.status === 'success') {
        actions.setSubmitting(false);
        onSuccess();
      } else {
        handleError(resp, actions);
      }
    } catch (e) {
      handleError(e, actions);
    }
  }

  async function handleResend() {
    try {
      await deliverTokenForMFA({ challenge: challenge.id });
    } catch (e) {
      // Best-effort resend; the user can still enter a previously sent code.
    }
  }

  const renderButtons = formikProps => {
    const buttons = [
      {
        label: 'submit',
        capitalize: true,
        type: 'submit',
        onPress: e => {
          e.preventDefault();
          if (!formikProps.isSubmitting) {
            handleSubmit(formikProps.values, formikProps);
          }
        },
        disabled: !formikProps.isValid || formikProps.isSubmitting,
        loading: formikProps.isSubmitting,
      },
      ...(isSms
        ? [{ label: 'resend_code', variant: 'text', onPress: handleResend }]
        : []),
      {
        label: 'cancel',
        variant: 'text',
        onPress: () => onCancel(),
      },
    ];
    return <ButtonList items={buttons} layout={'vertical'} />;
  };

  return (
    <Modal
      open={!!challenge}
      close
      maxWidth={415}
      title={'verification_required'}
      titleCentered
      onDismiss={onCancel}>
      {challenge && (
        <Formik
          initialValues={{ otp: '' }}
          onSubmit={(values, actions) => handleSubmit(values, actions)}>
          {formikProps => (
            <Form>
              <View ph={1} pb={1} aI={'center'}>
                <PlaceholderImage name={isTotp ? 'mfa' : 'otp'} width={110} />
                <View mt={1}>
                  <Text
                    align={'center'}
                    id={
                      isTotp
                        ? 'please_enter_token_from_mfa_app'
                        : 'please_enter_otp_from_mobile'
                    }
                  />
                </View>
                <Input
                  field={{ ...inputs.otp, autoFocus: true }}
                  formikProps={formikProps}
                />
                <ErrorOutput>
                  {formikProps.status && formikProps.status.error}
                </ErrorOutput>
              </View>
              {renderButtons(formikProps)}
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
}
